import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
    Alert,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import SendIcon from "@mui/icons-material/Send";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import { API_URL } from "../config";
import SoftwareLicenseDevices from "./SoftwareLicenseDevices";

const emptyForm = {
    email: "",
    deviceId: "",
    edition: "core",
    planId: "",
    planType: "Annual",
    years: 1,
    sendEmail: true,
};

export default function SoftwareLicenses() {
    const [status, setStatus] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [issues, setIssues] = useState([]);
    const [lastBlob, setLastBlob] = useState(null);
    const [message, setMessage] = useState(null);
    const [busy, setBusy] = useState(false);
    const [tab, setTab] = useState(0);

    const load = useCallback(async () => {
        const [statusRes, issuesRes] = await Promise.all([
            axios.get(`${API_URL}/software-licenses/status`, { withCredentials: true }),
            axios.get(`${API_URL}/software-licenses/issues`, { withCredentials: true }),
        ]);
        setStatus(statusRes.data);
        setIssues(issuesRes.data.items || []);
        setForm((prev) => {
            if (prev.planId || !statusRes.data?.plans?.length) return prev;
            const annualCore = statusRes.data.plans.find((p) => p.edition === "core" && !p.test);
            const pick = annualCore || statusRes.data.plans[0];
            return {
                ...prev,
                planId: pick.planId,
                edition: pick.edition,
                planType: pick.planType || "Annual",
            };
        });
    }, []);

    useEffect(() => {
        load().catch((error) =>
            setMessage({ severity: "error", text: error.response?.data?.error || "Unable to load license tools." })
        );
    }, [load]);

    function onPlanChange(planId) {
        const plan = status?.plans?.find((p) => p.planId === planId);
        setForm((prev) => ({
            ...prev,
            planId,
            edition: plan?.edition || prev.edition,
            planType: plan?.planType || "Annual",
        }));
    }

    async function submit(event) {
        event.preventDefault();
        setBusy(true);
        setMessage(null);
        setLastBlob(null);
        try {
            const { data } = await axios.post(
                `${API_URL}/software-licenses/issue`,
                {
                    email: form.email.trim(),
                    deviceId: form.deviceId.trim(),
                    edition: form.edition,
                    planId: form.planId || null,
                    planType: form.planType,
                    years: Number(form.years) || 1,
                    sendEmail: Boolean(form.sendEmail),
                },
                { withCredentials: true }
            );
            setLastBlob(data.blob);
            setMessage({
                severity: "success",
                text: data.emailed
                    ? `License issued and emailed to ${data.payload.sub}.`
                    : "License issued. Copy the blob below (email was not sent).",
            });
            await load();
        } catch (error) {
            setMessage({
                severity: "error",
                text: error.response?.data?.error || "Unable to issue license.",
            });
        } finally {
            setBusy(false);
        }
    }

    async function copyBlob(text) {
        try {
            await navigator.clipboard.writeText(text);
            setMessage({ severity: "success", text: "License copied to clipboard." });
        } catch {
            setMessage({ severity: "error", text: "Could not copy to clipboard." });
        }
    }

    async function resend(id) {
        setBusy(true);
        setMessage(null);
        try {
            await axios.post(`${API_URL}/software-licenses/issues/${id}/resend`, {}, { withCredentials: true });
            setMessage({ severity: "success", text: "License email resent." });
            await load();
        } catch (error) {
            setMessage({
                severity: "error",
                text: error.response?.data?.error || "Unable to resend.",
            });
        } finally {
            setBusy(false);
        }
    }

    return (
        <Stack spacing={3}>
            <Stack spacing={0.5}>
                <Typography variant="overline" color="primary.main" sx={{ letterSpacing: ".16em", fontWeight: 800 }}>
                    SOFTWARE
                </Typography>
                <Typography variant="h4" sx={{ fontSize: { xs: "1.65rem", sm: "2.125rem" } }}>
                    Software licenses
                </Typography>
                <Typography color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
                    Device remaps and offline keys for subscribed desktop apps. Upload builds, branch notes, and
                    per-user or all-subscriber updates on App installers.
                </Typography>
            </Stack>

            <Tabs
                value={tab}
                onChange={(_e, value) => setTab(value)}
                variant="fullWidth"
                sx={{ minHeight: 42, "& .MuiTab-root": { minHeight: 42, py: 0.5 } }}
            >
                <Tab label="Monthly" />
                <Tab label="Offline" />
            </Tabs>

            {tab === 0 && <SoftwareLicenseDevices />}

            {tab === 1 && (
                <Stack spacing={3}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip
                            size="small"
                            label={status?.signingConfigured ? "Signing ready" : "Set OFFLINE_SIGN_SK"}
                            color={status?.signingConfigured ? "success" : "warning"}
                        />
                        <Chip
                            size="small"
                            label={status?.mailConfigured ? "Email ready" : "Configure RESEND or SMTP"}
                            color={status?.mailConfigured ? "success" : "warning"}
                        />
                    </Stack>

                    {message && <Alert severity={message.severity}>{message.text}</Alert>}

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Card>
                                <CardContent component="form" onSubmit={submit}>
                                    <Stack spacing={2}>
                                        <Typography variant="h6">Issue offline license</Typography>
                                        <TextField
                                            required
                                            label="Customer email"
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                                            helperText="Must match the email they use in MachineFlow activate"
                                        />
                                        <TextField
                                            required
                                            label="Device ID"
                                            value={form.deviceId}
                                            onChange={(e) => setForm({ ...form, deviceId: e.target.value })}
                                            helperText="From their MachineFlow activate screen"
                                        />
                                        <FormControl fullWidth>
                                            <InputLabel id="plan-label">Plan</InputLabel>
                                            <Select
                                                labelId="plan-label"
                                                label="Plan"
                                                value={form.planId}
                                                onChange={(e) => onPlanChange(e.target.value)}
                                            >
                                                {(status?.plans || []).map((plan) => (
                                                    <MenuItem key={plan.planId} value={plan.planId}>
                                                        {plan.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl fullWidth>
                                            <InputLabel id="edition-label">Edition</InputLabel>
                                            <Select
                                                labelId="edition-label"
                                                label="Edition"
                                                value={form.edition}
                                                onChange={(e) => setForm({ ...form, edition: e.target.value })}
                                            >
                                                <MenuItem value="core">core</MenuItem>
                                                <MenuItem value="premium">premium</MenuItem>
                                                <MenuItem value="dev">dev</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            label="Years"
                                            type="number"
                                            inputProps={{ min: 1, max: 10 }}
                                            value={form.years}
                                            onChange={(e) => setForm({ ...form, years: e.target.value })}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.sendEmail}
                                                    onChange={(e) => setForm({ ...form, sendEmail: e.target.checked })}
                                                    disabled={!status?.mailConfigured}
                                                />
                                            }
                                            label="Email license to customer"
                                        />
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                disabled={busy || !status?.signingConfigured}
                                                startIcon={form.sendEmail ? <SendIcon /> : <VpnKeyIcon />}
                                            >
                                                {form.sendEmail ? "Issue & email" : "Issue only"}
                                            </Button>
                                            <Button startIcon={<RefreshIcon />} onClick={() => load()} disabled={busy}>
                                                Refresh
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, md: 7 }}>
                            <Stack spacing={2}>
                                {lastBlob && (
                                    <Card>
                                        <CardContent>
                                            <Stack spacing={1.5}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                    <Typography variant="h6">Latest license</Typography>
                                                    <Button
                                                        size="small"
                                                        startIcon={<ContentCopyIcon />}
                                                        onClick={() => copyBlob(lastBlob)}
                                                    >
                                                        Copy
                                                    </Button>
                                                </Stack>
                                                <TextField
                                                    multiline
                                                    minRows={4}
                                                    value={lastBlob}
                                                    InputProps={{ readOnly: true }}
                                                    sx={{ "& textarea": { fontFamily: "ui-monospace, monospace", fontSize: 12 } }}
                                                />
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                )}

                                <Card>
                                    <CardContent>
                                        <Stack spacing={1.5}>
                                            <Typography variant="h6">Recently issued</Typography>
                                            {!issues.length && (
                                                <Typography color="text.secondary">No offline licenses issued yet.</Typography>
                                            )}
                                            {issues.map((row) => (
                                                <Stack
                                                    key={row.id}
                                                    direction={{ xs: "column", sm: "row" }}
                                                    justifyContent="space-between"
                                                    alignItems={{ sm: "center" }}
                                                    spacing={1}
                                                    sx={{ py: 1.25, borderBottom: 1, borderColor: "divider" }}
                                                >
                                                    <Stack spacing={0.25}>
                                                        <Typography fontWeight={700}>{row.email}</Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {row.edition} · {row.plan_type} · expires {String(row.expires_at || "").slice(0, 10)}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Device {String(row.device_id || "").slice(0, 12)}… ·{" "}
                                                            {row.emailed ? `emailed (${row.mail_provider || "ok"})` : "not emailed"} ·{" "}
                                                            {String(row.created_at || "").replace("T", " ").slice(0, 19)}
                                                        </Typography>
                                                    </Stack>
                                                    <Button
                                                        size="small"
                                                        disabled={busy || !status?.mailConfigured}
                                                        onClick={() => resend(row.id)}
                                                    >
                                                        Resend email
                                                    </Button>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Stack>
                        </Grid>
                    </Grid>
                </Stack>
            )}
        </Stack>
    );
}
